import React, { useState, useEffect, useCallback } from "react"
import { FileText, Trash2, Download, Plus, Share2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { useDocuments, useDeleteDocument } from "../hooks/useDocuments"
import { UploadDocumentModal } from "./UploadDocumentModal"
import { ShareDocumentDrawer } from "./ShareDocumentDrawer"
import { DocumentIcon } from "./DocumentIcon"
import { categoryLabel, formatSize, EXPIRY_BADGE } from "../constants"
import { useAuth } from "src/contexts/AuthContext"

// A document inherits access from its parent record (see DocumentPolicy):
// upload / delete need the parent resource's `update`. Maps the scope's
// singular type to the permission resource key; the org library (no scope)
// falls back to `leads`, which every CRM role can read.
const RESOURCE_FOR_SCOPE = { lead: "leads", deal: "deals", booking: "bookings", project: "projects" }

function ExpiryBadge({ status }) {
  const badge = EXPIRY_BADGE[status]
  if (!badge) return null
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${badge.className}`}>
      {badge.label}
    </span>
  )
}

// Reusable document panel embeddable on any parent record's detail page.
// `scope` is { type: "lead"|"deal"|"booking"|"project", id }, or null for the
// org library. `categories` constrains the upload category dropdown.
export function DocumentList({ scope, categories, emptyHint = "No documents attached", onChange, refreshKey = 0 }) {
  const { toast } = useToast()
  const { can } = useAuth()
  const { data, fetch, loading } = useDocuments(scope)
  const remove = useDeleteDocument()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [shareTarget, setShareTarget] = useState(null)

  const docResource = RESOURCE_FOR_SCOPE[scope?.type] || "leads"
  const canManageDocs = can(docResource, "update")

  const reload = useCallback(() => {
    fetch()
    onChange?.()
  }, [fetch, onChange])
  // Re-fetch on mount and whenever an external action (e.g. generating the
  // booking-confirmation letter) bumps refreshKey.
  useEffect(() => { fetch() }, [fetch, refreshKey])

  const docs = data?.documents

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.title || doc.file_name}"?`)) return
    try {
      await remove(doc.id)
      toast({ title: "Document deleted" })
      reload()
    } catch {
      toast({ variant: "destructive", title: "Could not delete document" })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Documents</h3>
        {canManageDocs && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setUploadOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Upload
          </Button>
        )}
      </div>

      {docs === undefined && loading ? (
        <div className="text-sm text-slate-400">Loading documents…</div>
      ) : !docs || docs.length === 0 ? (
        <div className="flex h-28 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 text-sm text-slate-400">
          <FileText className="h-5 w-5" /> {emptyHint}
        </div>
      ) : (
        <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
          {docs.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-4 py-3">
              <DocumentIcon contentType={doc.content_type} fileName={doc.file_name} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-800">{doc.title || doc.file_name}</span>
                  <ExpiryBadge status={doc.expiry_status} />
                </div>
                <div className="text-[11px] text-slate-400">
                  {categoryLabel(doc.category)}
                  {doc.byte_size ? ` · ${formatSize(doc.byte_size)}` : ""}
                  {doc.uploader?.full_name ? ` · ${doc.uploader.full_name}` : ""}
                </div>
              </div>
              {doc.is_shareable_with_customer && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShareTarget(doc)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                  title="Share"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
              {doc.url && (
                <a
                  href={doc.url} target="_blank" rel="noopener noreferrer"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </a>
              )}
              {canManageDocs && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(doc)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <UploadDocumentModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        scope={scope}
        categories={categories}
        onUploaded={reload}
      />

      <ShareDocumentDrawer
        document={shareTarget}
        open={!!shareTarget}
        onOpenChange={(next) => { if (!next) setShareTarget(null) }}
      />
    </div>
  )
}
